import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";

const PRESETS = [
  {
    label: "Today",
    getValue: () => {
      const today = new Date();
      return { from: today, to: today };
    },
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: yesterday, to: yesterday };
    },
  },
  {
    label: "This Week",
    getValue: () => {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return { from: startOfWeek, to: today };
    },
  },
  {
    label: "Last 7 Days",
    getValue: () => {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 6);
      return { from: lastWeek, to: today };
    },
  },
  {
    label: "This Month",
    getValue: () => {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: startOfMonth, to: today };
    },
  },
  {
    label: "Last 30 Days",
    getValue: () => {
      const today = new Date();
      const last30 = new Date(today);
      last30.setDate(today.getDate() - 29);
      return { from: last30, to: today };
    },
  },
  {
    label: "Last 3 Months",
    getValue: () => {
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() + 1); // Start from next day to avoid partial month
      return { from: threeMonthsAgo, to: today };
    },
  },
  {
    label: "Last 6 Months",
    getValue: () => {
      const today = new Date();
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      sixMonthsAgo.setDate(sixMonthsAgo.getDate() + 1); // Start from next day to avoid partial month
      return { from: sixMonthsAgo, to: today };
    },
  },
  {
    label: "This Year",
    getValue: () => {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return { from: startOfYear, to: today };
    },
  },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.toDateString() === b.toDateString();
}

function isInRange(date, from, to) {
  if (!from || !to) return false;
  const time = date.getTime();
  return time >= from.getTime() && time <= to.getTime();
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export function DateRangeFilter({
  value,
  onChange,
  placeholder = "Select dates",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState(value || { from: null, to: null });
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [leftMonth, setLeftMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const containerRef = useRef(null);

  const rightMonth = {
    year: leftMonth.month === 11 ? leftMonth.year + 1 : leftMonth.year,
    month: leftMonth.month === 11 ? 0 : leftMonth.month + 1,
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      setRange(value);
    }
  }, [value]);

  const handleDateClick = (date) => {
    if (!selectingEnd || !range.from) {
      setRange({ from: date, to: null });
      setSelectingEnd(true);
    } else {
      if (date < range.from) {
        setRange({ from: date, to: range.from });
      } else {
        setRange({ from: range.from, to: date });
      }
      setSelectingEnd(false);
    }
  };

  const handlePresetClick = (preset) => {
    const newRange = preset.getValue();
    setRange(newRange);
    setSelectingEnd(false);
    if (onChange) onChange(newRange);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (onChange) onChange(range);
    setIsOpen(false);
  };

  const handleClear = () => {
    const emptyRange = { from: null, to: null };
    setRange(emptyRange);
    setSelectingEnd(false);
    if (onChange) onChange(emptyRange);
  };

  const navigateMonth = (direction) => {
    setLeftMonth((prev) => {
      if (direction === "prev") {
        return {
          year: prev.month === 0 ? prev.year - 1 : prev.year,
          month: prev.month === 0 ? 11 : prev.month - 1,
        };
      }
      return {
        year: prev.month === 11 ? prev.year + 1 : prev.year,
        month: prev.month === 11 ? 0 : prev.month + 1,
      };
    });
  };

  const renderCalendar = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div style={styles.calendarContainer}>
        <div style={styles.calendarHeader}>
          {MONTHS[month]} {year}
        </div>
        <div style={styles.daysHeader}>
          {DAYS.map((day) => (
            <div key={day} style={styles.dayHeader}>
              {day}
            </div>
          ))}
        </div>
        <div style={styles.daysGrid}>
          {days.map((date, idx) => {
            if (!date) {
              return <div key={`empty-${idx}`} style={styles.emptyDay} />;
            }

            const isStart = isSameDay(date, range.from);
            const isEnd = isSameDay(date, range.to);
            const isSelected = isStart || isEnd;
            const effectiveTo =
              selectingEnd && hoveredDate && range.from && !range.to
                ? hoveredDate
                : range.to;
            const inRange =
              range.from &&
              effectiveTo &&
              isInRange(
                date,
                range.from < effectiveTo ? range.from : effectiveTo,
                range.from < effectiveTo ? effectiveTo : range.from,
              ) &&
              !isSelected;
            const isToday = isSameDay(date, today);

            return (
              <button
                key={date.toISOString()}
                type="button"
                onClick={() => handleDateClick(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
                style={{
                  ...styles.dayButton,
                  ...(isSelected ? styles.dayButtonSelected : {}),
                  ...(inRange ? styles.dayButtonInRange : {}),
                  ...(isToday && !isSelected ? styles.dayButtonToday : {}),
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const displayText =
    range.from && range.to
      ? `${formatDate(range.from)} - ${formatDate(range.to)}`
      : range.from
        ? `${formatDate(range.from)} - ...`
        : placeholder;

  const hasValue = range.from !== null;

  const styles = {
    container: {
      position: "relative",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    triggerButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      borderRadius: "8px",
      border: "1px solid var(--light-gray)",
      fontSize: "14px",
      fontWeight: "500",
      minWidth: "160px",
      cursor: "pointer",
      transition: "all 0.15s ease",
      backgroundColor: "var(--background)",
    },
    triggerButtonWithValue: {
      borderColor: "var(--primary)",
      backgroundColor: "rgba(255, 90, 0, 0.05)",
    },
    triggerButtonWithoutValue: {
      color: "var(--gray)",
    },
    triggerButtonHover: {
      borderColor: "var(--primary)",
    },
    triggerText: {
      flex: 1,
      textAlign: "left",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#6b7280",
    },
    clearButton: {
      padding: "2px",
      borderRadius: "4px",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "var(--icon)",
      transition: "background-color 0.2s, color 0.2s",
    },
    clearButtonHover: {
      backgroundColor: "rgba(255, 90, 0, 0.1)",
      color: "var(--text)",
    },
    dropdown: {
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: "8px",
      backgroundColor: "var(--background)",
      border: "1px solid var(--light-gray)",
      borderRadius: "12px",
      boxShadow:
        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      zIndex: 50,
      overflow: "hidden",
      animation: "fadeIn 0.15s ease-out",
    },
    dropdownInner: {
      display: "flex",
    },
    presetsSidebar: {
      width: "144px",
      borderRight: "1px solid var(--gray)",
      backgroundColor: "var(--light-gray)",
      padding: "8px",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },
    presetsHeader: {
      padding: "6px 8px",
      fontSize: "11px",
      fontWeight: "600",
      color: "var(--gray)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    presetButton: {
      width: "100%",
      textAlign: "left",
      padding: "6px 8px",
      fontSize: "14px",
      borderRadius: "6px",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "var(--text)",
      transition: "background-color 0.2s, color 0.2s",
    },
    presetButtonHover: {
      backgroundColor: "var(--background)",
      color: "var(--primary)",
    },
    calendarsContainer: {
      padding: "16px",
    },
    calendarsHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
    },
    navButton: {
      padding: "6px",
      borderRadius: "6px",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "var(--icon)",
      transition: "background-color 0.2s, color 0.2s",
    },
    navButtonHover: {
      backgroundColor: "var(--light-gray)",
      color: "var(--text)",
    },
    selectingText: {
      fontSize: "14px",
      color: "var(--gray)",
    },
    calendarsGrid: {
      display: "flex",
      gap: "24px",
    },
    calendarContainer: {
      flex: 1,
      minWidth: "260px",
    },
    calendarHeader: {
      textAlign: "center",
      fontWeight: "500",
      fontSize: "14px",
      color: "var(--text)",
      marginBottom: "12px",
    },
    daysHeader: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "2px",
      marginBottom: "4px",
    },
    dayHeader: {
      textAlign: "center",
      fontSize: "12px",
      color: "var(--gray)",
      fontWeight: "500",
      padding: "6px 0",
    },
    daysGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "2px",
    },
    emptyDay: {
      height: "36px",
    },
    dayButton: {
      height: "36px",
      width: "100%",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.15s ease",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "var(--text)",
    },
    dayButtonSelected: {
      backgroundColor: "var(--primary)",
      color: "var(--background)",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    },
    dayButtonInRange: {
      backgroundColor: "rgba(255, 90, 0, 0.1)",
      color: "var(--primary)",
    },
    dayButtonToday: {
      outline: "1px solid var(--primary)",
    },
    dayButtonHover: {
      backgroundColor: "var(--light-gray)",
    },
    dropdownFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingTop: "16px",
      marginTop: "16px",
      borderTop: "1px solid var(--gray)",
    },
    daysSelectedText: {
      fontSize: "14px",
      color: "var(--gray)",
    },
    footerButtons: {
      display: "flex",
      gap: "8px",
    },
    cancelButton: {
      padding: "6px 12px",
      fontSize: "14px",
      borderRadius: "6px",
      border: "1px solid var(--gray)",
      background: "none",
      cursor: "pointer",
      color: "var(--text)",
      transition: "background-color 0.2s",
    },
    cancelButtonHover: {
      backgroundColor: "var(--light-gray)",
    },
    applyButton: {
      padding: "6px 12px",
      fontSize: "14px",
      borderRadius: "6px",
      border: "none",
      backgroundColor: "var(--primary)",
      color: "var(--background)",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    applyButtonHover: {
      backgroundColor: "var(--secondary)",
    },
    applyButtonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    icon: {
      width: "16px",
      height: "16px",
      color: "var(--icon)",
    },
    smallIcon: {
      width: "14px",
      height: "14px",
    },
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...styles.triggerButton,
          ...(hasValue
            ? styles.triggerButtonWithValue
            : styles.triggerButtonWithoutValue),
        }}
        onMouseEnter={(e) => {
          if (!hasValue) {
            e.currentTarget.style.borderColor = "var(--primary)";
          }
        }}
        onMouseLeave={(e) => {
          if (!hasValue) {
            e.currentTarget.style.borderColor = "var(--light-gray)";
            e.currentTarget.style.backgroundColor = "var(--background)";
          }
        }}
      >
        <Calendar style={styles.icon} />
        <span style={styles.triggerText}>{displayText}</span>
        {hasValue && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={styles.clearButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 90, 0, 0.1)";
              e.currentTarget.style.color = "var(--text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--icon)";
            }}
          >
            <X style={styles.smallIcon} />
          </button>
        )}
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownInner}>
            {/* Presets sidebar */}
            <div style={styles.presetsSidebar}>
              <div style={styles.presetsHeader}>Quick Select</div>
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  style={styles.presetButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--background)";
                    e.currentTarget.style.color = "var(--primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--text)";
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Calendars */}
            <div style={styles.calendarsContainer}>
              <div style={styles.calendarsHeader}>
                <button
                  type="button"
                  onClick={() => navigateMonth("prev")}
                  style={styles.navButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--icon)";
                  }}
                >
                  <ChevronLeft style={styles.icon} />
                </button>
                <div style={styles.selectingText}>
                  {selectingEnd && range.from
                    ? "Select end date"
                    : "Select start date"}
                </div>
                <button
                  type="button"
                  onClick={() => navigateMonth("next")}
                  style={styles.navButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--icon)";
                  }}
                >
                  <ChevronRight style={styles.icon} />
                </button>
              </div>

              <div style={styles.calendarsGrid}>
                {renderCalendar(leftMonth.year, leftMonth.month)}
                {renderCalendar(rightMonth.year, rightMonth.month)}
              </div>

              {/* Footer */}
              <div style={styles.dropdownFooter}>
                <div style={styles.daysSelectedText}>
                  {range.from && range.to && (
                    <span>
                      {Math.ceil(
                        (range.to.getTime() - range.from.getTime()) /
                          (1000 * 60 * 60 * 24),
                      ) + 1}{" "}
                      days selected
                    </span>
                  )}
                </div>
                <div style={styles.footerButtons}>
                  <button
                    type="button"
                    onClick={() => {
                      handleClear();
                      setIsOpen(false);
                    }}
                    style={styles.cancelButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "var(--light-gray)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!range.from || !range.to}
                    style={{
                      ...styles.applyButton,
                      ...(!range.from || !range.to
                        ? styles.applyButtonDisabled
                        : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (range.from && range.to) {
                        e.currentTarget.style.backgroundColor =
                          "var(--secondary)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (range.from && range.to) {
                        e.currentTarget.style.backgroundColor =
                          "var(--primary)";
                      }
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
